
import React from "react";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { pdfjs } from "react-pdf";
import { SidebarFileList } from "./SidebarFileList";
import { useTrainingCompletion } from "./useTrainingCompletion";
import { useTrainingContent } from "./hooks/useTrainingContent";
import { useTrainingInteractions } from "./hooks/useTrainingInteractions";
import { useTrainingSession } from "./hooks/useTrainingSession";
import { useTrainingContentTypes } from "./training/useTrainingContentTypes";
import { TrainingContent } from "./training/TrainingContent";
import { TrainingErrorView } from "./training/TrainingErrorView";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface UserTrainingProps {
  resourceId: string;
  topic?: string;
  company?: string;
  onComplete?: () => void;
}

export function UserTraining({ resourceId, topic = "Training", company = "Company", onComplete }: UserTrainingProps) {
  // Use our custom hooks
  const { isTrainee, isAdmin } = useTrainingSession();
  
  // Use the training content hook
  const {
    files,
    currentFileIndex,
    currentFile,
    loadingError,
    setLoadingError,
    freeResponseQuestions,
    fetchFreeResponseQuestions,
    handleFileChange
  } = useTrainingContent(resourceId);
  
  // Use the custom hook for tracking completion
  const {
    completedFiles,
    completedAt,
    isFileLocked,
    overallProgress,
    markFileCompleted,
    setCompletedFiles
  } = useTrainingCompletion(resourceId, files, isTrainee, isAdmin);
  
  // Use the training interactions hook
  const {
    quiz,
    isLoadingQuiz,
    quizCompleted,
    quizAttempt,
    hasAttemptedFreeResponseFetch,
    setHasAttemptedFreeResponseFetch,
    handleContentComplete,
    handleQuizComplete,
    handleDragDropComplete
  } = useTrainingInteractions(
    resourceId,
    currentFile,
    currentFileIndex,
    isTrainee,
    isAdmin,
    setCompletedFiles,
    fetchFreeResponseQuestions,
    markFileCompleted
  );
  
  // Get content types
  const { isQuizView, isFreeResponseView, isDragDropView, isLinkingView } = 
    useTrainingContentTypes(currentFile);
  
  // Render error state if resources couldn't be loaded
  if (loadingError && files.length === 0) {
    return <TrainingErrorView onReload={() => window.location.reload()} />;
  }

  // Handle user completing the training
  const handleTrainingComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className="flex h-full">
      <SidebarFileList
        files={files}
        completedFiles={completedFiles}
        currentFileIndex={currentFileIndex}
        isTrainee={isTrainee}
        isAdmin={isAdmin}
        isFileLocked={isFileLocked}
        handleFileChange={handleFileChange}
        overallProgress={overallProgress}
        completedAt={completedAt}
        resourceId={resourceId}
        topic={topic}
        company={company}
      />

      <TrainingContent
        currentFile={currentFile}
        currentFileIndex={currentFileIndex}
        files={files}
        isQuizView={isQuizView}
        isFreeResponseView={isFreeResponseView}
        isDragDropView={isDragDropView}
        isLinkingView={isLinkingView}
        isTrainee={isTrainee}
        isAdmin={isAdmin}
        isFileLocked={isFileLocked}
        completedFiles={completedFiles}
        overallProgress={overallProgress}
        resourceId={resourceId}
        markFileCompleted={markFileCompleted}
        quiz={quiz}
        isLoadingQuiz={isLoadingQuiz}
        quizCompleted={quizCompleted}
        quizAttempt={quizAttempt}
        handleQuizComplete={handleQuizComplete}
        handleContentComplete={handleContentComplete}
        handleDragDropComplete={handleDragDropComplete}
        loadingError={loadingError}
        setLoadingError={setLoadingError}
        freeResponseQuestions={freeResponseQuestions}
        handleFileChange={handleFileChange}
      />
    </div>
  );
}
